import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useMemo } from "react";
import { VoiceConversation, } from "@elevenlabs/client";
import { ConversationContext } from "./ConversationContext.js";
const EMPTY_FREQUENCY_DATA = new Uint8Array(0);
export const ConversationControlsContext = createContext(null);
/**
 * Reads from `ConversationContext` and provides stable action references to
 * `ConversationControlsContext`. Must be rendered inside a `ConversationProvider`.
 */
export function ConversationControlsProvider({ children, }) {
    const ctx = useContext(ConversationContext);
    if (!ctx) {
        throw new Error("ConversationControlsProvider must be rendered inside a ConversationProvider");
    }
    const { conversationRef } = ctx;
    const getConversation = useCallback(() => {
        const conversation = conversationRef.current;
        if (!conversation) {
            throw new Error("No active conversation. Call startSession() first.");
        }
        return conversation;
    }, [conversationRef]);
    const sendUserMessage = useCallback((text) => {
        getConversation().sendUserMessage(text);
    }, [getConversation]);
    const sendMultimodalMessage = useCallback((options) => {
        getConversation().sendMultimodalMessage(options);
    }, [getConversation]);
    const uploadFile = useCallback((file) => {
        return getConversation().uploadFile(file);
    }, [getConversation]);
    const sendContextualUpdate = useCallback((text, options) => {
        getConversation().sendContextualUpdate(text, options);
    }, [getConversation]);
    const sendUserActivity = useCallback(() => {
        getConversation().sendUserActivity();
    }, [getConversation]);
    const sendMCPToolApprovalResult = useCallback((toolCallId, isApproved) => {
        getConversation().sendMCPToolApprovalResult(toolCallId, isApproved);
    }, [getConversation]);
    const setVolume = useCallback((options) => {
        getConversation().setVolume(options);
    }, [getConversation]);
    const changeInputDevice = useCallback(async (config) => {
        const conversation = getConversation();
        if (conversation instanceof VoiceConversation) {
            return await conversation.changeInputDevice(config);
        }
        throw new Error("Device switching is only available for voice conversations");
    }, [getConversation]);
    const changeOutputDevice = useCallback(async (config) => {
        const conversation = getConversation();
        if (conversation instanceof VoiceConversation) {
            return await conversation.changeOutputDevice(config);
        }
        throw new Error("Device switching is only available for voice conversations");
    }, [getConversation]);
    const getInputByteFrequencyData = useCallback(() => {
        return (conversationRef.current?.getInputByteFrequencyData() ??
            EMPTY_FREQUENCY_DATA);
    }, [conversationRef]);
    const getOutputByteFrequencyData = useCallback(() => {
        return (conversationRef.current?.getOutputByteFrequencyData() ??
            EMPTY_FREQUENCY_DATA);
    }, [conversationRef]);
    const getInputVolume = useCallback(() => {
        return conversationRef.current?.getInputVolume() ?? 0;
    }, [conversationRef]);
    const getOutputVolume = useCallback(() => {
        return conversationRef.current?.getOutputVolume() ?? 0;
    }, [conversationRef]);
    const getId = useCallback(() => {
        return getConversation().getId();
    }, [getConversation]);
    const value = useMemo(() => ({
        startSession: ctx.startSession,
        endSession: ctx.endSession,
        sendUserMessage,
        sendMultimodalMessage,
        uploadFile,
        sendContextualUpdate,
        sendUserActivity,
        sendMCPToolApprovalResult,
        setVolume,
        changeInputDevice,
        changeOutputDevice,
        getInputByteFrequencyData,
        getOutputByteFrequencyData,
        getInputVolume,
        getOutputVolume,
        getId,
    }), [
        ctx.startSession,
        ctx.endSession,
        sendUserMessage,
        sendMultimodalMessage,
        uploadFile,
        sendContextualUpdate,
        sendUserActivity,
        sendMCPToolApprovalResult,
        setVolume,
        changeInputDevice,
        changeOutputDevice,
        getInputByteFrequencyData,
        getOutputByteFrequencyData,
        getInputVolume,
        getOutputVolume,
        getId,
    ]);
    return (_jsx(ConversationControlsContext.Provider, { value: value, children: children }));
}
/**
 * Returns stable action references for controlling the conversation.
 * All function references are stable and will never cause re-renders.
 *
 * Must be used within a `ConversationProvider`.
 */
export function useConversationControls() {
    const ctx = useContext(ConversationControlsContext);
    if (!ctx) {
        throw new Error("useConversationControls must be used within a ConversationProvider");
    }
    return ctx;
}
//# sourceMappingURL=ConversationControls.js.map